import { useState } from "react";
import { Plus, Copy, Search, BookOpen, Trash2, Edit2, Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { toast } from "sonner";

export interface Clause {
  id: string;
  name: string;
  content: string;
  category: string;
  tags: string[];
}

const defaultClauses: Clause[] = [
  {
    id: "1",
    name: "Confidentiality",
    content: "Both parties agree to keep all proprietary information, trade secrets, and business strategies confidential. This obligation shall survive the termination of this agreement for a period of {{confidentiality_period}} years.",
    category: "legal",
    tags: ["NDA", "privacy", "standard"],
  },
  {
    id: "2",
    name: "Payment Terms - Net 30",
    content: "Payment is due within thirty (30) days of invoice date. Late payments shall incur interest at the rate of {{late_fee_percentage}}% per month on the outstanding balance.",
    category: "payment",
    tags: ["invoicing", "standard"],
  },
  {
    id: "3",
    name: "Intellectual Property",
    content: "All intellectual property created during the course of this engagement shall remain the exclusive property of {{ip_owner}}. The client is granted a non-exclusive, perpetual license to use the deliverables.",
    category: "legal",
    tags: ["IP", "ownership"],
  },
  {
    id: "4",
    name: "Termination",
    content: "Either party may terminate this agreement with {{notice_period}} days written notice. Upon termination, the client shall pay for all work completed up to the termination date.",
    category: "legal",
    tags: ["exit", "standard"],
  },
  {
    id: "5",
    name: "Limitation of Liability",
    content: "The maximum liability under this agreement shall not exceed the total fees paid under this contract. Neither party shall be liable for indirect, incidental, or consequential damages.",
    category: "legal",
    tags: ["liability", "risk"],
  },
  {
    id: "6",
    name: "Force Majeure",
    content: "Neither party shall be liable for failure to perform due to circumstances beyond reasonable control, including but not limited to acts of God, war, terrorism, natural disasters, or government actions.",
    category: "legal",
    tags: ["risk", "standard"],
  },
  {
    id: "7",
    name: "Warranty",
    content: "{{company_name}} warrants that all services will be performed in a professional and workmanlike manner. This warranty shall be valid for {{warranty_period}} days following delivery.",
    category: "service",
    tags: ["guarantee", "quality"],
  },
  {
    id: "8",
    name: "Non-Solicitation",
    content: "During the term of this agreement and for {{non_solicit_period}} months thereafter, neither party shall solicit or hire employees of the other party without prior written consent.",
    category: "legal",
    tags: ["HR", "employees"],
  },
];

interface ClauseLibraryProps {
  onInsertClause?: (clause: Clause) => void;
  mode?: "manage" | "select";
}

export function ClauseLibrary({ onInsertClause, mode = "manage" }: ClauseLibraryProps) {
  const [clauses, setClauses] = useState<Clause[]>(defaultClauses);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [newClause, setNewClause] = useState<Partial<Clause>>({
    name: "",
    content: "",
    category: "legal",
    tags: [],
  });

  const categories = ["all", ...Array.from(new Set(clauses.map((c) => c.category)))];

  const filteredClauses = clauses.filter((clause) => {
    const matchesSearch =
      clause.name.toLowerCase().includes(search.toLowerCase()) ||
      clause.content.toLowerCase().includes(search.toLowerCase()) ||
      clause.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
    const matchesCategory = categoryFilter === "all" || clause.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const handleAddClause = () => {
    if (!newClause.name || !newClause.content) return;
    const clause: Clause = {
      id: crypto.randomUUID(),
      name: newClause.name,
      content: newClause.content,
      category: newClause.category || "legal",
      tags: newClause.tags || [],
    };
    setClauses([...clauses, clause]);
    setNewClause({ name: "", content: "", category: "legal", tags: [] });
    setIsAddOpen(false);
    toast.success("Clause added to library");
  };

  const handleDeleteClause = (id: string) => {
    setClauses(clauses.filter((c) => c.id !== id));
    toast.success("Clause removed");
  };

  const handleCopyClause = (clause: Clause) => {
    navigator.clipboard.writeText(clause.content);
    toast.success("Clause copied to clipboard");
  };

  const handleInsertClause = (clause: Clause) => {
    if (onInsertClause) {
      onInsertClause(clause);
      toast.success(`"${clause.name}" inserted`);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold">Clause Library</h3>
        </div>
        <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" />
              Add Clause
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Clause</DialogTitle>
              <DialogDescription>
                Create a reusable clause for your contract templates
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Clause Name</label>
                <Input
                  value={newClause.name}
                  onChange={(e) => setNewClause({ ...newClause, name: e.target.value })}
                  placeholder="e.g., Confidentiality Agreement"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Select
                  value={newClause.category}
                  onValueChange={(v) => setNewClause({ ...newClause, category: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="legal">Legal</SelectItem>
                    <SelectItem value="payment">Payment</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="delivery">Delivery</SelectItem>
                    <SelectItem value="support">Support</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Content</label>
                <Textarea
                  value={newClause.content}
                  onChange={(e) => setNewClause({ ...newClause, content: e.target.value })}
                  placeholder="Enter clause content. Use {{field_name}} for merge fields."
                  rows={5}
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Tags (comma-separated)</label>
                <Input
                  value={newClause.tags?.join(", ")}
                  onChange={(e) =>
                    setNewClause({
                      ...newClause,
                      tags: e.target.value.split(",").map((t) => t.trim()).filter(Boolean),
                    })
                  }
                  placeholder="e.g., standard, NDA, privacy"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddClause} disabled={!newClause.name || !newClause.content}>
                Add Clause
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search clauses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-40">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {categories.map((cat) => (
              <SelectItem key={cat} value={cat} className="capitalize">
                {cat === "all" ? "All Categories" : cat}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <ScrollArea className="h-[400px]">
        <div className="space-y-3 pr-4">
          {filteredClauses.map((clause) => (
            <Card key={clause.id} className="group">
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-sm font-medium">{clause.name}</CardTitle>
                    <Badge variant="outline" className="mt-1 text-xs capitalize">
                      {clause.category}
                    </Badge>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => handleCopyClause(clause)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    {mode === "select" && onInsertClause && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-primary"
                        onClick={() => handleInsertClause(clause)}
                      >
                        <Plus className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {mode === "manage" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-destructive"
                        onClick={() => handleDeleteClause(clause.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground line-clamp-3">{clause.content}</p>
                {clause.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {clause.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="text-[10px] px-1.5 py-0">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
          {filteredClauses.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No clauses found</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
